import { ExternalLink, ChevronDown, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { ArtifactType } from '@/hooks/useIdeaArtifacts';

const MVP_DISPLAY_ORDER = [
  { key: 'two_week_scope', label: 'Two-Week Scope', type: 'text' },
  { key: 'must_have_features', label: 'Must-Have Features', type: 'list' },
  { key: 'should_have_features', label: 'Should-Have Features', type: 'list' },
  { key: 'nice_to_have', label: 'Nice to Have', type: 'list' },
  { key: 'excluded_features', label: 'Excluded Features', type: 'list' },
  { key: 'tech_complexity_estimate', label: 'Tech Complexity', type: 'badge' },
  { key: 'key_dependencies', label: 'Key Dependencies', type: 'list' },
  { key: 'next_action', label: 'Next Action', type: 'text' },
] as const;

const MONETIZATION_DISPLAY_ORDER = [
  { key: 'primary_model', label: 'Primary Model', type: 'text' },
  { key: 'why_primary', label: 'Why This Model', type: 'text' },
  { key: 'alternative_models', label: 'Alternative Models', type: 'list' },
  { key: 'pricing_strategy', label: 'Pricing Strategy', type: 'text' },
  { key: 'assumptions', label: 'Key Assumptions', type: 'list' },
  { key: 'recommended_first_test', label: 'First Test', type: 'text' },
  { key: 'risks', label: 'Risks', type: 'list' },
  { key: 'next_action', label: 'Next Action', type: 'text' },
] as const;

function getFieldsForType(artifactType: ArtifactType) {
  return artifactType === 'mvp_scope' ? MVP_DISPLAY_ORDER : MONETIZATION_DISPLAY_ORDER;
}

export interface SourceRef {
  title: string;
  url: string;
  snippet: string;
}

const confidenceConfig = {
  High: { icon: ShieldCheck, className: 'bg-green-500/10 text-green-700 dark:text-green-400' },
  Medium: { icon: Shield, className: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400' },
  Low: { icon: ShieldAlert, className: 'bg-red-500/10 text-red-700 dark:text-red-400' },
} as const;

export function ConfidenceBadge({ level }: { level: 'High' | 'Medium' | 'Low' }) {
  const config = confidenceConfig[level] || confidenceConfig.Medium;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md text-xs font-medium px-2 py-1 ${config.className}`}>
      <Icon className="h-3.5 w-3.5" />
      {level} confidence
    </span>
  );
}

export function SourcesAccordion({ sources }: { sources: SourceRef[] }) {
  if (!sources || sources.length === 0) return null;

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full py-2">
        <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
        Sources ({sources.length})
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="space-y-3 pt-2">
          {sources.map((source, idx) => (
            <li key={idx} className="rounded-md border border-border bg-muted/30 p-3 space-y-1">
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
              >
                {source.title}
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
              {source.snippet && (
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                  {source.snippet}
                </p>
              )}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ContentRenderer({ content, artifactType }: { content: Record<string, unknown>; artifactType: ArtifactType }) {
  const fields = getFieldsForType(artifactType);

  return (
    <div className="space-y-5">
      {fields.map(({ key, label, type }) => {
        const value = content[key];
        if (!value || (Array.isArray(value) && value.length === 0)) return null;

        return (
          <div key={key} className="space-y-1.5">
            <h4 className="text-sm font-medium text-foreground">{label}</h4>
            {type === 'list' && Array.isArray(value) ? (
              <ul className="space-y-1">
                {(value as string[]).map((item, idx) => (
                  <li key={idx} className="text-sm text-foreground/80 flex items-start gap-2">
                    <span className="text-muted-foreground mt-1 shrink-0">•</span>
                    <span className="break-words">{item}</span>
                  </li>
                ))}
              </ul>
            ) : type === 'badge' ? (
              <span className="inline-block rounded-md bg-primary/10 text-primary text-xs font-medium px-2 py-1">
                {String(value)}
              </span>
            ) : (
              <p className="text-sm text-foreground/80 leading-relaxed break-words">{String(value)}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function formatContentAsText(content: Record<string, unknown>, artifactType: ArtifactType): string {
  const fields = getFieldsForType(artifactType);
  return fields.map(({ key, label }) => {
    const value = content[key];
    if (!value) return '';
    if (Array.isArray(value)) {
      return `${label}:\n${(value as string[]).map(v => `  • ${v}`).join('\n')}`;
    }
    return `${label}: ${String(value)}`;
  }).filter(Boolean).join('\n\n');
}
